import { useEffect, useState } from "react";
import { api, fmtNGN } from "@/lib/api";
import { Loader2, Trophy, Layers, Users, Crown, Medal } from "lucide-react";

const RANK_STYLES = [
  "bg-gold/20 text-gold border-gold/40",
  "bg-slate-300/20 text-slate-400 border-slate-300/40",
  "bg-amber-700/20 text-amber-600 border-amber-700/40",
];

function RankBadge({ i }) {
  if (i === 0) return <div className="h-9 w-9 rounded-full grid place-items-center border bg-gold/20 text-gold border-gold/40"><Crown className="h-4 w-4" /></div>;
  if (i < 3) return <div className={`h-9 w-9 rounded-full grid place-items-center border ${RANK_STYLES[i]}`}><Medal className="h-4 w-4" /></div>;
  return <div className="h-9 w-9 rounded-full grid place-items-center border border-border text-muted-foreground font-heading font-bold text-sm">{i + 1}</div>;
}

function Board({ title, icon: Icon, rows, valueLabel, renderValue, testid, emptyText }) {
  return (
    <div className="bg-card border border-border rounded-3xl p-6" data-testid={testid}>
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-2xl navy-gradient grid place-items-center shrink-0"><Icon className="h-5 w-5 text-white" /></div>
        <div>
          <h2 className="font-heading text-lg font-bold">{title}</h2>
          <p className="text-xs text-muted-foreground">{valueLabel}</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={r.username + i} data-testid={`${testid}-row-${i}`}
              className={`flex items-center gap-3 rounded-2xl p-3 border transition-colors ${r.is_me ? "bg-gold/10 border-gold/40" : "bg-secondary/40 border-transparent"}`}>
              <RankBadge i={i} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">
                  {r.username}{r.is_me && <span className="ml-2 text-[10px] font-bold text-gold uppercase tracking-wide">You</span>}
                </p>
              </div>
              <span className="font-heading font-bold text-gold tabular-nums shrink-0">{renderValue(r)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Leaderboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/leaderboard").then((r) => setData(r.data)).catch(() => setData({ top_collectors: [], top_referrers: [] })); }, []);
  if (!data) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="leaderboard-page">
      <div className="flex items-center gap-3">
        <Trophy className="h-8 w-8 text-gold" />
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold">Leaderboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Top members across AR Club. Collect more and refer friends to climb the ranks.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Board
          title="Top Collectors" icon={Layers} valueLabel="Ranked by artworks held"
          rows={data.top_collectors} testid="top-collectors"
          renderValue={(r) => `${r.count} art${r.count === 1 ? "" : "s"}`}
          emptyText="No collectors yet. Buy an artwork to get on the board!"
        />
        <Board
          title="Top Referrers" icon={Users} valueLabel="Ranked by members referred"
          rows={data.top_referrers} testid="top-referrers"
          renderValue={(r) => `${r.count} • ${fmtNGN(r.earned)}`}
          emptyText="No referrers yet. Share your code to lead the board!"
        />
      </div>
    </div>
  );
}
